import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs, 
  addDoc, 
  doc, 
  getDoc,
  where,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebaseService';
import { getCurrentUser } from './authService';
import {
  ActivityHistoryDocument,
  ActivityHistoryQueryOptions,
  ActivityHistoryResult,
  AddActivityLogData,
  RestoreDataOptions,
  ShareBookActionType,
  ActivityHistoryFilter,
  ActivityHistoryStats
} from '@/types/activityHistory';

class ActivityHistoryService {
  // 활동 기록 조회 (페이지네이션)
  async getActivityHistory(
    shareBookId: string, 
    options: ActivityHistoryQueryOptions = {}
  ): Promise<ActivityHistoryResult> {
    try {
      const { lastDoc = null, limitCount = 30, actionType, userId } = options;
      
      
      let q = query(
        collection(db, 'sharedBooks', shareBookId, 'history'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      // 필터 적용
      if (actionType) {
        q = query(q, where('actionType', '==', actionType));
      }
      
      if (userId) {
        q = query(q, where('userId', '==', userId));
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      
      
      const histories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      })) as ActivityHistoryDocument[];


      return {
        histories,
        lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
        hasMore: histories.length === limitCount
      };
    } catch (error) {
      // 컬렉션이 존재하지 않거나 권한이 없는 경우 빈 결과 반환
      console.error('활동 기록 조회 실패 (컬렉션 없음 또는 권한 없음):', error);
      return {
        histories: [],
        lastDoc: null,
        hasMore: false
      };
    }
  }

  // 활동 기록 추가
  async addActivityLog(data: AddActivityLogData): Promise<void> {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('로그인이 필요합니다.');
      }

      const historyData = {
        bookId: data.shareBookId,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || '알 수 없는 사용자',
        actionType: data.actionType,
        description: data.description,
        createdAt: new Date(),
        metadata: {
          restorable: false,
          ...data.metadata
        }
      };

      await addDoc(collection(db, 'sharedBooks', data.shareBookId, 'history'), historyData);
    } catch (error) {
      console.error('활동 기록 추가 실패:', error);
      throw error;
    }
  }

  // 데이터 복원
  async restoreData(options: RestoreDataOptions): Promise<void> {
    try {
      const { historyId, shareBookId, metadata } = options;
      const { deletedData, oldData, accountNumber, actionType } = metadata;
      
      // 현재 사용자 확인
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('로그인이 필요합니다.');
      }


      // 실제 데이터 복원 로직
      const restoreData = oldData || deletedData;
      
      if (!restoreData) {
        throw new Error('복원할 데이터가 없습니다.');
      }

      // Firestore에 데이터 복원
      const { firestoreService } = await import('./firestoreService');
      
      // 복원할 데이터를 Firebase 구조에 맞게 변환
      const expenseData = {
        name: restoreData.name,
        incomeType: restoreData.incomeType,
        money: restoreData.money,
        content: restoreData.content,
        merchantName: restoreData.merchantName,
        accountNumber: accountNumber || restoreData.accountNumber,
        taxType: restoreData.taxType || 'taxable',
        createdTime: restoreData.createdTime || new Date(),
        ownerId: currentUser.uid,
      };


      // sharedBooks에 데이터 추가
      const restoredExpenseId = await firestoreService.addExpense(currentUser.uid, expenseData, true);
      
      
      // 복원 활동 기록 추가
      await this.addActivityLog({
        shareBookId,
        actionType: 'DATA_RESTORED',
        description: `'${deletedData?.name || '데이터'}' ${deletedData?.incomeType || ''}을 ${oldData ? '수정' : '복원'}했습니다 (${this.formatAmount(deletedData?.money || 0)})`,
        metadata: {
          restorable: false,
          restoredFrom: historyId,
          originalActionType: actionType,
          accountNumber,
          isUpdate: !!oldData,
          restoredExpenseId
        }
      });

    } catch (error) {
      console.error('❌ 데이터 복원 실패:', error);
      throw error;
    }
  }

  // 활동 기록 필터링 조회
  async getFilteredActivityHistory(
    shareBookId: string,
    filter: ActivityHistoryFilter,
    options: ActivityHistoryQueryOptions = {}
  ): Promise<ActivityHistoryResult> {
    try {
      const { lastDoc = null, limitCount = 30 } = options;
      
      let q = query(
        collection(db, 'sharedBooks', shareBookId, 'history'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      // 액션 타입 필터
      if (filter.actionTypes && filter.actionTypes.length > 0) {
        q = query(q, where('actionType', 'in', filter.actionTypes));
      }

      // 사용자 필터
      if (filter.userIds && filter.userIds.length > 0) {
        q = query(q, where('userId', 'in', filter.userIds));
      }

      // 복원 가능 여부 필터
      if (filter.restorable !== undefined) {
        q = query(q, where('metadata.restorable', '==', filter.restorable));
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      
      let histories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      })) as ActivityHistoryDocument[];

      // 날짜 범위 필터 (클라이언트 사이드에서 처리)
      if (filter.dateRange) {
        histories = histories.filter(history => {
          let createdAt: Date;
          if (history.createdAt instanceof Date) {
            createdAt = history.createdAt;
          } else if (history.createdAt?.toDate) {
            createdAt = history.createdAt.toDate();
          } else {
            createdAt = new Date(history.createdAt as any);
          }
          return createdAt >= filter.dateRange!.start && createdAt <= filter.dateRange!.end;
        });
      }

      return {
        histories,
        lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
        hasMore: histories.length === limitCount
      };
    } catch (error) {
      // 컬렉션이 존재하지 않거나 권한이 없는 경우 빈 결과 반환
      console.error('필터된 활동 기록 조회 실패 (컬렉션 없음 또는 권한 없음):', error);
      return {
        histories: [],
        lastDoc: null,
        hasMore: false
      };
    }
  }

  // 활동 기록 통계 조회
  async getActivityHistoryStats(shareBookId: string): Promise<ActivityHistoryStats> {
    try {
      const result = await this.getActivityHistory(shareBookId, { limitCount: 1000 });
      
      const actionTypeCounts: Record<ShareBookActionType, number> = {} as any;
      const userCounts: Record<string, number> = {};
      
      result.histories.forEach(history => {
        // 액션 타입별 카운트
        actionTypeCounts[history.actionType] = (actionTypeCounts[history.actionType] || 0) + 1;
        
        // 사용자별 카운트
        userCounts[history.userId] = (userCounts[history.userId] || 0) + 1;
      });

      return {
        totalCount: result.histories.length,
        actionTypeCounts,
        userCounts,
        recentActivity: result.histories.slice(0, 10) // 최근 10개
      };
    } catch (error) {
      // 데이터가 없거나 컬렉션이 존재하지 않는 경우 빈 통계 반환
      console.error('활동 기록이 없거나 컬렉션이 존재하지 않습니다:', error);
      return {
        totalCount: 0,
        actionTypeCounts: {} as any,
        userCounts: {},
        recentActivity: []
      };
    }
  }

  // 실시간 활동 기록 구독
  subscribeToActivityHistory(
    shareBookId: string,
    callback: (histories: ActivityHistoryDocument[]) => void,
    options: ActivityHistoryQueryOptions = {}
  ): Unsubscribe {
    const { limitCount = 30 } = options;
    
    const q = query(
      collection(db, 'sharedBooks', shareBookId, 'history'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
      const histories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      })) as ActivityHistoryDocument[];
      
      callback(histories);
    });
  }

  // 금액 포맷팅
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('ko-KR').format(amount);
  }

  // 동적 설명 생성 (매출/지출 구분)
  generateDynamicDescription(actionType: ShareBookActionType, metadata?: any, originalDescription?: string): string {
    const isRevenue = this.isRevenueActivity(actionType, metadata);
    
    // metadata에서 데이터 추출
    const data = metadata?.deletedData || metadata?.oldData;
    const name = data?.name || '항목';
    const money = data?.money || 0;
    
    const formattedAmount = this.formatAmount(money);
    const typeText = isRevenue ? '매출' : '지출';
    
    // 액션 타입에 따른 동적 설명 생성
    switch (actionType) {
      case 'EXPENSE_ADDED':
        return `'${name}' ${typeText}을 추가했습니다 (${formattedAmount})`;
      case 'EXPENSE_UPDATED':
        return `'${name}' ${typeText}을 수정했습니다 (${formattedAmount})`;
      case 'EXPENSE_DELETED':
        return `'${name}' ${typeText}을 삭제했습니다 (${formattedAmount})`;
      case 'REPEAT_EXPENSE_ADDED':
        return `'${name}' 반복 ${typeText}을 추가했습니다 (${formattedAmount})`;
      case 'REPEAT_EXPENSE_UPDATED':
        return `'${name}' 반복 ${typeText}을 수정했습니다 (${formattedAmount})`;
      case 'REPEAT_EXPENSE_DELETED':
        return `'${name}' 반복 ${typeText}을 삭제했습니다 (${formattedAmount})`;
      default:
        return originalDescription || `${typeText} 작업이 수행되었습니다`;
    }
  }

  // 활동 타입별 아이콘 반환 (매출/지출 구분)
  getActivityIcon(actionType: ShareBookActionType, metadata?: any): string {
    // 매출과 지출을 구분하는 로직 추가
    const isRevenue = this.isRevenueActivity(actionType, metadata);
    
    const iconMap: Record<ShareBookActionType, string> = {
      // 공유가계부 관리
      CREATED: '📚',
      UPDATED: '✏️',
      SHARE_BOOK_UPDATED: '✏️',
      SHARE_BOOK_DELETED: '🗑️',
      DELETED: '🗑️',
      
      // 멤버 관리
      MEMBER_ADDED: '👥',
      MEMBER_REMOVED: '👤',
      MEMBER_PERMISSION_CHANGED: '🔐',
      MEMBER_PERMISSION_UPDATED: '🔐',
      
      // 초대 관련
      INVITATION_SENT: '📤',
      INVITATION_ACCEPTED: '✅',
      INVITATION_REJECTED: '❌',
      INVITATION_CANCELLED: '🚫',
      
      // 데이터 CRUD - 매출과 지출 구분
      EXPENSE_ADDED: isRevenue ? '💰' : '💸', // 매출: 💰, 지출: 💸
      EXPENSE_UPDATED: isRevenue ? '✏️💰' : '✏️💸', // 매출 수정: ✏️💰, 지출 수정: ✏️💸
      EXPENSE_DELETED: isRevenue ? '🗑️💰' : '🗑️💸', // 매출 삭제: 🗑️💰, 지출 삭제: 🗑️💸
      REPEAT_EXPENSE_ADDED: isRevenue ? '🔄💰' : '🔄💸',
      REPEAT_EXPENSE_UPDATED: isRevenue ? '🔄💰' : '🔄💸',
      REPEAT_EXPENSE_DELETED: isRevenue ? '🔄💰' : '🔄💸',
      DATA_RESTORED: '↩️',
      
      // 시스템 관련
      DATA_SYNCED: '🔄',
      DATA_CONFLICT: '⚠️',
      BACKUP_CREATED: '💾',
      BACKUP_RESTORED: '💾',
      SHARED: '🔗',
      UNSHARED: '🔓',
      SUSPENDED: '⏸️',
      ACTIVATED: '▶️'
    };
    
    return iconMap[actionType] || '📝';
  }

  // 매출 활동인지 확인하는 헬퍼 메서드
  private isRevenueActivity(actionType: ShareBookActionType, metadata?: any): boolean {
    // EXPENSE 관련 액션이 아닌 경우 false 반환
    if (!actionType.includes('EXPENSE') && !actionType.includes('REPEAT_EXPENSE')) {
      return false;
    }

    // metadata에서 incomeType 확인 (여러 경로 시도)
    const incomeType = metadata?.deletedData?.incomeType || 
                      metadata?.oldData?.incomeType ||
                      metadata?.incomeType;

    if (incomeType) {
      // 매출 판단: 한국어 '매출' 또는 영어 'income'
      return incomeType === '매출' || incomeType === 'income';
    }

    // 기본값은 지출로 처리
    return false;
  }

  // 활동 타입을 한국어로 번역 (매출/지출 구분)
  translateActionType(actionType: ShareBookActionType, metadata?: any): string {
    // 매출과 지출을 구분하는 로직 추가
    const isRevenue = this.isRevenueActivity(actionType, metadata);
    
    const translationMap: Record<ShareBookActionType, string> = {
      // 공유가계부 관리
      CREATED: '공유가계부 생성',
      UPDATED: '공유가계부 수정',
      SHARE_BOOK_UPDATED: '공유가계부 정보 수정',
      SHARE_BOOK_DELETED: '공유가계부 삭제',
      DELETED: '공유가계부 삭제',
      
      // 멤버 관리
      MEMBER_ADDED: '멤버 추가',
      MEMBER_REMOVED: '멤버 제거',
      MEMBER_PERMISSION_CHANGED: '멤버 권한 변경',
      MEMBER_PERMISSION_UPDATED: '멤버 권한 수정',
      
      // 초대 관련
      INVITATION_SENT: '초대 발송',
      INVITATION_ACCEPTED: '초대 수락',
      INVITATION_REJECTED: '초대 거절',
      INVITATION_CANCELLED: '초대 취소',
      
      // 데이터 CRUD - 매출과 지출 구분
      EXPENSE_ADDED: isRevenue ? '매출 추가' : '지출 추가',
      EXPENSE_UPDATED: isRevenue ? '매출 수정' : '지출 수정',
      EXPENSE_DELETED: isRevenue ? '매출 삭제' : '지출 삭제',
      REPEAT_EXPENSE_ADDED: isRevenue ? '반복 매출 추가' : '반복 지출 추가',
      REPEAT_EXPENSE_UPDATED: isRevenue ? '반복 매출 수정' : '반복 지출 수정',
      REPEAT_EXPENSE_DELETED: isRevenue ? '반복 매출 삭제' : '반복 지출 삭제',
      DATA_RESTORED: '데이터 복원',
      
      // 시스템 관련
      DATA_SYNCED: '데이터 동기화',
      DATA_CONFLICT: '데이터 충돌',
      BACKUP_CREATED: '백업 생성',
      BACKUP_RESTORED: '백업 복원',
      SHARED: '공유 시작',
      UNSHARED: '공유 중단',
      SUSPENDED: '일시정지',
      ACTIVATED: '활성화'
    };
    
    return translationMap[actionType] || actionType;
  }

  // 활동 타입별 색상 반환 (매출/지출 구분)
  getActivityColor(actionType: ShareBookActionType, metadata?: any): string {
    // 매출과 지출을 구분하는 로직 추가
    const isRevenue = this.isRevenueActivity(actionType, metadata);
    
    const colorMap: Record<ShareBookActionType, string> = {
      // 공유가계부 관리
      CREATED: '#4CAF50',
      UPDATED: '#2196F3',
      SHARE_BOOK_UPDATED: '#2196F3',
      SHARE_BOOK_DELETED: '#F44336',
      DELETED: '#F44336',
      
      // 멤버 관리
      MEMBER_ADDED: '#4CAF50',
      MEMBER_REMOVED: '#F44336',
      MEMBER_PERMISSION_CHANGED: '#FF9800',
      MEMBER_PERMISSION_UPDATED: '#FF9800',
      
      // 초대 관련
      INVITATION_SENT: '#2196F3',
      INVITATION_ACCEPTED: '#4CAF50',
      INVITATION_REJECTED: '#F44336',
      INVITATION_CANCELLED: '#9E9E9E',
      
      // 데이터 CRUD - 매출과 지출 구분
      EXPENSE_ADDED: isRevenue ? '#4CAF50' : '#FF9800', // 매출: 초록색, 지출: 주황색
      EXPENSE_UPDATED: isRevenue ? '#2196F3' : '#FF9800', // 매출 수정: 파란색, 지출 수정: 주황색
      EXPENSE_DELETED: isRevenue ? '#F44336' : '#F44336', // 삭제는 모두 빨간색
      REPEAT_EXPENSE_ADDED: isRevenue ? '#4CAF50' : '#FF9800',
      REPEAT_EXPENSE_UPDATED: isRevenue ? '#2196F3' : '#FF9800',
      REPEAT_EXPENSE_DELETED: isRevenue ? '#F44336' : '#F44336',
      DATA_RESTORED: '#FF9800',
      
      // 시스템 관련
      DATA_SYNCED: '#2196F3',
      DATA_CONFLICT: '#FF5722',
      BACKUP_CREATED: '#9C27B0',
      BACKUP_RESTORED: '#9C27B0',
      SHARED: '#4CAF50',
      UNSHARED: '#9E9E9E',
      SUSPENDED: '#FF9800',
      ACTIVATED: '#4CAF50'
    };
    
    return colorMap[actionType] || '#9E9E9E';
  }
}

export default ActivityHistoryService;
